/**
 * Tool-call UI for the lake agent's ``lake_hybrid_search``.
 *
 * Visually mirrors :file:`HybridSearchToolUI.tsx` (same `ToolCallCard` /
 * `ToolCallHeader` shell, same expandable hit list) but renders Typesense
 * lake hits and routes clicks to the lake document detail sheet on `/data`.
 */

import { makeAssistantToolUI } from '@assistant-ui/react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { LakeHybridSearchView } from '@/components/agent/tools/LakeHybridSearchView'
import {
  type LakeHybridSearchArgs,
  type LakeHybridSearchResult,
  parseResult,
} from './lake-hybrid-search'

type Status = { type: 'running' | 'complete' | 'incomplete' | string }

interface LakeHybridSearchRenderProps {
  args: LakeHybridSearchArgs
  result?: LakeHybridSearchResult | string
  status: Status
}

/**
 * Connected `lake_hybrid_search` tool card: parses the backend payload, wires
 * the per-hit document-open action to the router, and hands pure data to
 * LakeHybridSearchView.
 */
function LakeHybridSearchRender({ args, result, status }: LakeHybridSearchRenderProps) {
  const navigate = useNavigate()

  const running = status.type === 'running'
  const parsed = useMemo(() => parseResult(result), [result])
  const errored = Boolean(parsed?.error) || status.type === 'incomplete'

  // The lake page reads ?doc=<id> and opens its detail sheet. Pushing this
  // URL while already on /data is a search-param-only update.
  const onOpenDocument = (documentId: string) => {
    navigate(`/data?doc=${encodeURIComponent(documentId)}`, { replace: false })
  }

  return (
    <LakeHybridSearchView
      args={args}
      parsed={parsed}
      running={running}
      errored={errored}
      onOpenDocument={onOpenDocument}
    />
  )
}

export const LakeHybridSearchToolUI = makeAssistantToolUI<
  LakeHybridSearchArgs,
  LakeHybridSearchResult | string
>({
  toolName: 'lake_hybrid_search',
  render: LakeHybridSearchRender,
})
